import _                               from 'lodash'
import { ReactElement, ReactNode, useEffect, useRef, useState } from 'react'
import { useNotifier }                 from 'hooks/notification'
import { useCustomRef }                from 'hooks/customref'
import { useTranslator }               from 'hooks/translator'
import Report                          from 'helpers/report'
import { useDebouncedState }           from 'utils/utils'
import { toRegex }                     from 'utils/option-utils'
import { Box, Typography }             from '@mui/material'
import PageStats                       from 'components/table/page/PageStats'
import MenuBar                         from 'components/table/page/MenuBar'
import LoadingTable                    from 'components/table/LoadingTable'
import ToolbarProvider, { ToolbarFunctions } from 'contexts/ToolbarContext'
import { useSearchVariable } from 'hooks/search'
import { useLocation, useSearchParams } from 'react-router-dom'
import { Section } from 'types/view'

type LargeTableProps = {
  description: string
  // rendering table data
  columns: { headerName: string, field: string, cellProps: any, type : string}[]
  dataLoader: DataLoader
  section?: Section
  // styling
  containerSx?: any
  tableSx?: any
  largeTableSx?: any
  // configurations
  pageSize?: number
  filterable?: boolean
  filterColumns?: string[]
  filterType?: FilterType
  disablePaging?: boolean
  disableFilter?: boolean
  disableReload?: boolean
  disableToolbar?: boolean
  lazy?: boolean
  extraMenuItems?: (toolbarFunctions: ToolbarFunctions) => ReactElement[]
  rowAction?: (row: any) => ReactNode
}

type DataLoader = (
  page: Page,
  errorCallback: (message: string, reason: string) => void,
  dataCallback: (data: Data) => void,
) => Cancellable

type Data = {
  rows: any[]             // the filtered rows
  count: number           // the total number of rows
  page: Page
  //pageCount: number // TODO misleading name; this apparently is the number of rows before local filtering
}

export type Page = {
  pageNum: number         // the current page number
  pageSize: number        // the number of rows per page
  filter: SearchOptions
  filterType: FilterType
  action: Action
}

export type SearchOptions = {
  search_text: string
  sort_setting?: SortSetting
  num_filters?: NumericFilter[]
  text_filters?: TextFilter[]
  date_filters?: TemporalFilter[]
}

type SortSetting = {
  column: string,
  direction: SortDirection
}

type SortDirection = 'ASCENDING' | 'DESCENDING'

type NumericFilter = {
  column: string
  operator: NumericOperator
  value: string
}

type TemporalFilter = {
  column: string
  operator: NumericOperator
  value: string
}

type NumericOperator = 'EQUALS' | 'GREATER_THAN' | 'LESS_THAN'

type TextFilter = {
  column: string
  operator: TextOperator
  value: string
}

type TextOperator = 'EQUALS' | 'CONTAINS' | 'STARTS' | 'ENDS'


type FilterType = 'remote' | 'local'

type Action = 'main' | 'reload' | 'first' | 'previous' | 'next' | 'last' | 'filter'

export type Status = {
  busy: boolean
  with?: Action
  request?: Cancellable
}

type Cancellable = {
  cancel: () => void
}

type Setter<T> = (value: T) => void

const LargeTable = ({
  description,
  // rendering table data
  columns = [],
  dataLoader,
  section,
  // styling
  containerSx = {},
  tableSx     = {},
  largeTableSx = {},
  // configurations
  pageSize: givenPageSize = 100,
  filterable = false,
  filterType = 'remote',
  filterColumns = [],
  extraMenuItems = _f => [],
  rowAction,
  lazy = false,
  disableFilter,
  disablePaging,
  disableReload,
  disableToolbar
}: LargeTableProps) => {
  const pageSize = disablePaging && 999999 || givenPageSize
  const {translator} = useTranslator()
  const notifier = useNotifier()
  const [page, pageSetter] = useState<Page>({ pageNum: 0, pageSize, filter: {search_text: ""}, filterType, action: 'main' })
  const [data, setData] = useState<Data>({ rows: [], count: 0, page: {...page, pageNum: 0 }, /*pageCount: -1*/ })
  const [pageable, setPageable] = useState(false)
  const [filter, setFilter] = useState<SearchOptions>({search_text: ''})
  const tableRef = useRef(null)
  const [status, setStatus] = useState<Status>({ busy: false })
  const busyWith = (action: Action): boolean => status.busy && status.with === action

  const location = useLocation(); //needed to observe url and update filters
  const pageParam            = useSearchVariable('p')
  const [_, setSearchParams] = useSearchParams()


  const setPage = !lazy
    ? pageSetter
    : (newPage: Page) => {
      setSearchParams(params => {
        if (pageParam != newPage.pageNum.toString()) {
          params.set('p', newPage.pageNum.toString())
        } else {
            console.log("set page %o", newPage)
            pageSetter(newPage)
          }
          return params
        })
    }

  const activeFilterColumns = filterColumns.length === 0 ? columns.filter(column => column.headerName).map(column => column.field) : filterColumns
  const toolbarFunctions = createToolbarFunctions(data, status, setStatus, setPage)
  const showToolbar = !disableToolbar && ((!disablePaging && pageable) || !disableFilter)

  // add functions to be visible from outside of this component
  const customRef = useCustomRef()
  customRef?.setCurrent({
    deleteLine: (id: string) => setData(data => ({...data, rows: data.rows.filter((row: any) => row.id != id)}))
  })

  // perform a request when the request info changes
  useEffect(() => {
    const pageChanged = data.page !== page
    if (pageChanged || page.action === 'reload') {
      const loadingDone = () => {
        setStatus({ busy: false, with: undefined })
      }
      const errorHandler = (message: string, reason: string) => {
        loadingDone()
        console.error(message, reason)
        Report.from(message, translator, { category: Report.backend }).addToNotifier(notifier)
      }
      const dataHandler = (result: any) => {
        loadingDone()
        console.log("LargeTable: handle data for %s: result=%o", description, result)
        const rows = filterType === 'local' ? filterRows(result.rows, activeFilterColumns, page.filter) : result.rows
        setData({ ...result, rows, page })
      }

      status.request?.cancel() // cancel a running request, if any
      console.debug("LargeTable: loading data for page=%o, data.page=%o", page, data.page)
      const request = dataLoader(page, errorHandler, dataHandler)
      setStatus({ busy: true, with: page.action, request })
    }
  },[page])

  // detects when url changes and updates filters
  const updateFilterWithUrl = () => {
    const params = new URLSearchParams(location.search);
    var search_text : string = "";
    var newNumberFilters : NumericFilter[] = []
    var newTemporalFilters : TemporalFilter[] = []
    var newTextFilters : TextFilter[] = []
    var newSortSetting : SortSetting | undefined = undefined
    params.forEach((value, key, _) => {
      const column = columns.find(col => col.field == key);
      if (!column) {
        if ("sort" == key) {
          const direction_str = value.slice(0, 3);
          const column_str = value.slice(4);
          if (columns.some(col => col.field == column_str)) {
            switch (direction_str) {
              case 'asc' : newSortSetting = {column : column_str, direction: 'ASCENDING'}; break;
              case 'des' : newSortSetting = {column : column_str, direction: 'DESCENDING'}; break;
              default: console.debug("Invalid sort directon '%s' in url", direction_str);
            }
          } else {
            console.debug("Invalid sort column '%s' in url", column_str);
          }
        } else if ("q" == key) {
          search_text = value;
        }
        return;
      };
      value = decodeURIComponent(value)

      const operator_str = value.slice(0, 2);
      const filter_txt = value.slice(3);
      if (["text"].includes(column.type)) {
        switch (operator_str) {
          case 'eq' : newTextFilters.push({column : key, operator :"EQUALS",   value : filter_txt}); break;
          case 'be' : newTextFilters.push({column : key, operator :"STARTS",   value : filter_txt}); break;
          case 'en' : newTextFilters.push({column : key, operator :"ENDS",     value : filter_txt}); break;
          case 'ct' : newTextFilters.push({column : key, operator :"CONTAINS", value : filter_txt}); break;
        }
      } else if (["integer", "decimal"].includes(column.type)) {
        switch (operator_str) {
          case 'eq' : newNumberFilters.push({column : key, operator :"EQUALS",       value : filter_txt}); break;
          case 'gt' : newNumberFilters.push({column : key, operator :"GREATER_THAN", value : filter_txt}); break;
          case 'lt' : newNumberFilters.push({column : key, operator :"LESS_THAN",    value : filter_txt}); break;
        }
      } else if (["date", "datetime", "time"].includes(column.type)) {
        switch (operator_str) {
          case 'eq' : newTemporalFilters.push({column : key, operator :"EQUALS",       value : filter_txt}); break;
          case 'gt' : newTemporalFilters.push({column : key, operator :"GREATER_THAN", value : filter_txt}); break;
          case 'lt' : newTemporalFilters.push({column : key, operator :"LESS_THAN",    value : filter_txt}); break;
        }
      }}
    )
    setFilter({search_text : filter.search_text, 
               num_filters: newNumberFilters, 
               date_filters: newTemporalFilters, 
               text_filters: newTextFilters, 
               sort_setting: newSortSetting})
  }
  useEffect(updateFilterWithUrl, [location.search]);

  // instigate a page change when the filter changes
  useEffect( () => {
    if (filter != page.filter) {
      setPage({...page, pageNum: 0, filter, action: 'filter' })
    }
  }, [filter])

  // active pagination buttons
  useEffect( () => {
    const hasCount = Boolean(data.count >= 0)
    const enablePaging = data.page.pageNum > 0 ||
      (hasCount && data.count > data.page.pageSize) /*||
      (!hasCount && data.pageCount === data.page.pageSize*)*/

    if (!pageable && enablePaging && !disablePaging)
      setPageable(enablePaging)

    // scroll to top
    if (tableRef.current) {
      // @ts-ignore
      tableRef.current.scrollTo({top: 0, left: 0})
    }
  }, [data])

  const actionColumn = {headerName: '', field: '__action', cellProps: {width: '40px'}, type: '__action'}
  const richColumns = rowAction === undefined ? columns : columns.concat([actionColumn])

  // TODO ref={tableRef} on LoadingTable
  return (
    <ToolbarProvider toolbarFunctions={toolbarFunctions}>
      <Box id="large-table-container" sx={{ display: "flex", maxHeight: '100%', maxWidth: '100%', flexFlow: 'column', ...largeTableSx }}>
        <MenuBar
          showToolbar={showToolbar}
          filter={filter}
          setFilter={setFilter}
          pageable={pageable}
          disableFilter={disableFilter ?? false}
          disableReload={disableReload ?? false}
          status={status}
          extraMenuItems={extraMenuItems}
          lazySearch={lazy}
        />
        <Box id="loading-table-container" sx={{width: "100%", flexShrink: 1, maxWidth: "100%", display: "flex", flexGrow: 1, overflow: 'hidden'}}>
            <LoadingTable columns={richColumns}
                          filterable={filterable}
                          rows={data.rows}
                          section={section}
                          loading={busyWith('main')}
                          rowAction={rowAction}
                          containerSx={containerSx}
                          tableSx={tableSx} />
        </Box>
        <PageStats pageable={pageable} data={data} />
        <EmptyTableMessage enable={!status.busy && data.count <= 0 && data.rows?.length <= 0}/>
      </Box>
    </ToolbarProvider>
  )
}

const EmptyTableMessage = ({message, enable}: {message?: string, enable: boolean}) => {
  const {t} = useTranslator()
  if (!enable) return null
  return (
    <Box sx={{display: "flex", marginX: "auto", marginY: "20px"}}>
      <Typography variant='h5'>
        {message || t('table.empty')}
      </Typography>
    </Box>
  )
}

function filterRows(rows: any[], filterColumns: string[], filter: SearchOptions) {
  if (!filter.search_text) return rows

  const regex = toRegex(filter.search_text)
  const check = (value: string) => regex.test(value)
  const filterRow = (row: any)  => filterColumns.some(column   => check(_.get(row.cells, column)))
  //TODO numeric filters?
  return rows.filter(filterRow)
}

const createToolbarFunctions = (data: any, status: Status, setStatus: Setter<Status>, setPage: Setter<Page>) => {
  const enabled  = data.page.pageNum >= 0
  const hasCount = data?.count >= 0
  const lastPage = Math.floor(Number(data.count) / Number(data.page.pageSize)) + (Number(data.count) % Number(data.page.pageSize) === 0 ? -1 : 0)
  const previousFilter = data.page.filter

  return {
    setPage: (pageNum: number, filter?: SearchOptions) => { setPage({...data.page, filter: filter??previousFilter, pageNum}) },
    reload: data.page.pageNum >= 0
      ? () => {
        setPage({...data.page, action: 'reload'})
      }
      : undefined,

    filter: () => {
      if (status.busy) setStatus({ busy: false, with: undefined })
    },

    first: enabled && data.page.pageNum > 0
      ? () => {
          setPage({...data.page, pageNum: 0, action: 'first'})
        }
      : undefined,

    last: enabled && hasCount && lastPage > data.page.pageNum
      ? () => {
          setPage({...data.page, pageNum: lastPage, action: 'last'})
        }
      : undefined,

    previous: enabled && data.page.pageNum > 0
      ? () => {
          setPage({...data.page, pageNum: data.page.pageNum - 1, action: 'previous'})
        }
      : undefined,

    next: enabled && (
        (hasCount  && data.count > (data.rows.length + data.page.pageNum * data.page.pageSize) ||
        (!hasCount && data.rows.length % data.page.pageSize === 0))
      )
      ? () => {
          setPage({...data.page, pageNum: data.page.pageNum + 1, action: 'next'})
        }
      : undefined,
  }
}

export default LargeTable
